import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ApiClient from '../utils/ApiClient';
import { PrinterIcon, ArrowPathIcon, TagIcon, Cog6ToothIcon, Square3Stack3DIcon } from '@heroicons/react/24/outline';
import CancelConfirmedShipmentModal from '../components/Common/CancelConfirmedShipmentModal';
import {
    printShipmentLabels, openShipmentLabelsPdf,
    printAllShipmentLabels, openAllShipmentLabelsPdf,
    printShipmentItemLabels, printAllShipmentItemLabels,
    printPalletLabels,
    DEFAULT_LABEL_PAGE_TYPE, PAGE_TYPES,
} from '../components/Common/PrintLabelsAction';
import { ShippingMethodChip } from './ShipmentRuns';

// Task 48 (owner request, verbatim): "once a shipment is confirmed, we need the ability to print
// out the UPS Label and the merchant label from the UI. Should we have a 'confirmed shipment'
// page that allows this functionality?" -- this is that page. NOTE: for Amazon-partnered UPS
// small-parcel shipments the UPS carrier label and the FBA box ("merchant") label come TOGETHER
// in one PackageLabel PDF per box -- printing "labels" here prints that combined document.
// Backed by GET /api/shipment-planner/confirmed (list) and
// GET /api/shipment-planner/labels/{planId}/{shipmentId} (server-side proxied PDF stream).
// No polling -- confirmations are rare; there's a manual Refresh button instead.
//
// Task 57 (owner, verbatim): "sometimes there are multiple labels/boxes per shipment - instead of
// clicking each button, can we have a print all labels button that automatically opens the print
// dialogue box for each box that is part of that shipment. Also the paper type will always be
// 4 x 6 - please auto select that as the default." Owner clarification: "print all" spans every
// SHIPMENT in the workflow too (an ASIN can get 5 shipments) -- GET .../labels/{planId}/all merges
// every shipment's box-label PDF server-side. Per-shipment print buttons remain below the
// run-level "print all" for reprints. Owner addition #2: a second DYMO-550 "item labels" (FNSKU)
// action at both levels, via GET .../item-labels/{planId}/{shipmentId|all}.

function formatDateTime(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function StatusPill({ status }) {
    const s = String(status || '').toUpperCase();
    let cls = 'bg-gray-100 text-gray-600';
    if (s === 'CONFIRMED') cls = 'bg-green-100 text-green-800';
    else if (s === 'CANCELLED' || s === 'SUPERSEDED') cls = 'bg-gray-200 text-gray-600';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>{s || 'UNKNOWN'}</span>;
}

// Task 77 (owner, verbatim): "on the confirmed shipments page - the shipped shipments should be
// greyed out." Amazon's live per-shipment lifecycle status (amazon_spapi_inboundplan_shipment,
// synced hourly -- see ConfirmedShipmentsService) drives this, distinct from the workflow-level
// `run.status` StatusPill above. Statuses meaning the shipment has physically LEFT the building
// render muted; WORKING/READY_TO_SHIP (still being prepped) stay full-contrast at the top.
export const SHIPMENT_LEFT_BUILDING_STATUSES = new Set([
    'SHIPPED', 'IN_TRANSIT', 'DELIVERED', 'RECEIVING', 'CLOSED',
]);

export function isShipmentGreyed(ship) {
    return SHIPMENT_LEFT_BUILDING_STATUSES.has(String(ship?.shipmentStatus || '').toUpperCase());
}

/** Active (still-being-prepped) shipments first, then greyed ones by most-recent status change. */
export function sortShipmentsForDisplay(shipments) {
    return [...(shipments || [])].sort((a, b) => {
        const aGreyed = isShipmentGreyed(a);
        const bGreyed = isShipmentGreyed(b);
        if (aGreyed !== bGreyed) return aGreyed ? 1 : -1;
        if (!aGreyed) return 0; // keep original order among active shipments
        const at = a.shipmentStatusUpdatedAt ? new Date(a.shipmentStatusUpdatedAt).getTime() : 0;
        const bt = b.shipmentStatusUpdatedAt ? new Date(b.shipmentStatusUpdatedAt).getTime() : 0;
        return bt - at; // most-recent first
    });
}

const SHIPMENT_STATUS_PILL_CLASS = {
    WORKING: 'bg-blue-100 text-blue-800',
    READY_TO_SHIP: 'bg-blue-100 text-blue-800',
};

function ShipmentStatusPill({ status }) {
    if (!status) return null;
    const s = String(status).toUpperCase();
    const cls = SHIPMENT_LEFT_BUILDING_STATUSES.has(s)
        ? 'bg-gray-200 text-gray-500'
        : (SHIPMENT_STATUS_PILL_CLASS[s] || 'bg-gray-100 text-gray-600');
    return <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${cls}`}>{s}</span>;
}

function ProductsCell({ asins }) {
    if (!asins || asins.length === 0) return <span className="text-gray-400">—</span>;
    const first = asins[0];
    const fullList = asins
        .map(a => `${a.name ? a.name + ' — ' : ''}${a.asin}${a.qty != null ? ` ×${a.qty}` : ''}`)
        .join('\n');
    return (
        <div className="min-w-0 max-w-[18rem]" title={fullList}>
            <div className="text-gray-700 truncate">
                {first.name || first.asin}
                {asins.length > 1 && <span className="text-gray-400"> +{asins.length - 1} more</span>}
            </div>
            <div className="font-mono text-[11px] text-gray-400 truncate">
                {asins.map(a => a.asin).join(', ')}
            </div>
            {/* Task 80 (owner: shipments "MUST NEVER" be created as used): the FNSKU + condition
                each ASIN will print on its item label. Amazon derives both from the plan's MSKU, so
                this page — the one with the "Print item labels" button — is the last place a used
                FNSKU can be caught. Used renders red with a warning icon. */}
            {asins.some(a => a.fnsku || a.conditionLabel) && (
                <div className="mt-0.5 space-y-0.5">
                    {asins.filter(a => a.fnsku || a.conditionLabel).map(a => (
                        <div
                            key={a.asin}
                            className={`text-[11px] flex items-center gap-1 ${
                                a.newCondition === false ? 'text-red-700 font-semibold' : 'text-gray-500'}`}
                            title={a.newCondition === false
                                ? `${a.sku} is a USED-condition SKU — its item label will print "${a.conditionLabel}"`
                                : undefined}
                        >
                            {a.newCondition === false && <span aria-hidden="true">&#9888;</span>}
                            <span className="font-mono">{a.fnsku || '—'}</span>
                            <span>{a.conditionLabel}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Task 62 (owner, verbatim): "on the top line of each workflow, show the 365 day average shipping
// per unit, and the shipping per unit for these shipments" + owner addition "also add remaining
// margin after FBA and Amazon referral fee". Rendered in the workflow's header row (Shipping
// column): "Shipping $X.XX/unit · 365d avg $Y.YY/unit" -- green when this workflow beats the
// average, red when above -- and "Margin after fees & freight: $Z.ZZ/unit (N%)". The backend
// blends two average sources (per-ASIN calibrate_inbound_shipping_costs history, stalled
// 2026-06-16, + our own confirmed runs' transport costs); avgShippingDataThrough carries the
// honest freshness date, shown when the data is more than a week old.
function fmtMoney(v) {
    return v == null ? null : `$${Number(v).toFixed(2)}`;
}

const STALE_AVG_MS = 7 * 24 * 60 * 60 * 1000;

function ShippingEconomics({ run }) {
    const spu = run.shippingPerUnit;
    const avg = run.avgShippingPerUnit;
    const through = run.avgShippingDataThrough || null;
    const isStale = through && (Date.now() - new Date(through).getTime()) > STALE_AVG_MS;

    if (spu == null && avg == null && run.marginPerUnit == null) return null;

    let spuClass = 'text-gray-600';
    if (spu != null && avg != null && spu !== avg) {
        spuClass = spu < avg ? 'text-green-600 font-medium' : 'text-red-600 font-medium';
    }

    const shippingTooltip = [
        spu != null
            ? `This workflow: ${fmtMoney(run.transportCost)} total partnered-carrier transport cost ÷ ${run.totalUnits ?? '?'} units.`
            : 'No transport cost recorded for this workflow.',
        avg != null
            ? `365d average source: ${run.avgShippingSource}${through ? `, data through ${through}` : ''}.`
            : 'No 365-day shipping history available yet.',
    ].join('\n');

    // Task 66: the margin's economics can come from three places, and a mixed multi-ASIN pool can
    // combine them -- historical (this workflow has a stale-but-real scorecard row) and estimated
    // (a different pooled ASIN never had scorecard economics at all) aren't mutually exclusive.
    const marginAsOfStale = run.marginAsOfDate
        && (Date.now() - new Date(run.marginAsOfDate).getTime()) > STALE_AVG_MS;
    const marginTooltipLines = [];
    if (run.marginEstimated) {
        marginTooltipLines.push(run.marginFbaFeeUnknown
            ? 'fees estimated via Amazon Fees API — Amazon has no package dimensions on file yet, ' +
              'so the FBA fee came back $0. This margin is an OPTIMISTIC UPPER BOUND (the real FBA ' +
              'fee is unknown), not a fact.'
            : 'fees estimated via Amazon Fees API — scorecard economics pending first stock receipt.');
        marginTooltipLines.push(
            'MAP price − estimated referral fee − estimated FBA fee − product cost (price list) − ' +
            `this workflow's inbound shipping per unit (${fmtMoney(spu) ?? 'unknown'}). ` +
            'Percent is of the weighted MAP price.');
    }
    if (run.marginHistorical) {
        marginTooltipLines.push(
            `economics from this ASIN's most recent historical scorecard row` +
            `${run.marginAsOfDate ? ` (as of ${run.marginAsOfDate})` : ''} — the latest scorecard ` +
            'date has no economics for it (e.g. it sold out of FBA).');
    }
    if (marginTooltipLines.length === 0) {
        marginTooltipLines.push(
            'MAP price − Amazon referral fee − FBA fee − product cost (latest ASIN scorecard, ' +
            'pool-weighted across this workflow\'s ASINs by units) − this workflow\'s inbound ' +
            `shipping per unit (${fmtMoney(spu) ?? 'unknown'}). Percent is of the weighted MAP price.`);
    }
    const marginTooltip = marginTooltipLines.join('\n');

    let marginClass = 'text-green-600';
    if (run.marginPerUnit != null) {
        if (run.marginPerUnit < 0) marginClass = 'text-red-600';
        else if (run.marginPct != null && run.marginPct < 10) marginClass = 'text-amber-600';
    }

    return (
        <div className="mt-1">
            <div className="text-[11px] whitespace-nowrap" title={shippingTooltip}>
                {spu != null
                    ? <span className={spuClass}>Shipping {fmtMoney(spu)}/unit</span>
                    : <span className="text-gray-400">Shipping —</span>}
                {avg != null && (
                    <span className="text-gray-400">
                        {' '}· 365d avg {fmtMoney(avg)}/unit{isStale ? ` (data through ${through})` : ''}
                    </span>
                )}
            </div>
            <div className={`text-[11px] ${run.marginPerUnit != null ? marginClass : 'text-gray-400'}`} title={marginTooltip}>
                {run.marginPerUnit != null
                    ? <>
                        Margin after fees & freight: {run.marginFbaFeeUnknown ? '≤ ' : ''}{fmtMoney(run.marginPerUnit)}/unit
                        {run.marginPct != null ? ` (${Number(run.marginPct).toFixed(1)}%)` : ''}
                        {run.marginFbaFeeUnknown
                            ? <span className="text-gray-400"> (FBA fee pending)</span>
                            : run.marginEstimated ? <span className="text-gray-400"> (estimated)</span> : null}
                        {run.marginHistorical && marginAsOfStale
                            ? <span className="text-gray-400"> (as of {run.marginAsOfDate})</span> : null}
                      </>
                    : 'margin data missing'}
            </div>
        </div>
    );
}

// Run-level "print everything in this workflow" bar: one click prints every box's label across
// every shipment (thermal 4×6 by default), plus a DYMO-550 item-labels action beside it. Sits
// above the per-shipment reprint rows.
function PrintAllBar({ run, shipmentCount, boxTotal, busyKey, onPrintAll, onPrintAllItems, onOpenAll, pageType, onPageTypeChange }) {
    const [open, setOpen] = useState(false);
    const busy = busyKey != null;
    return (
        <div className="relative flex flex-wrap items-center gap-2 px-1 pb-1">
            <span className="text-xs text-gray-400">All shipments:</span>
            <button
                onClick={() => onPrintAll(run)}
                disabled={busy}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                title="Fetch and print every box's label across every shipment in this workflow in one dialog, 4×6 thermal by default"
            >
                <PrinterIcon className="h-3.5 w-3.5" />
                {busyKey === 'ALL'
                    ? 'Printing…'
                    : `Print all labels — ${shipmentCount} shipment${shipmentCount === 1 ? '' : 's'}`
                        + (boxTotal != null ? `, ${boxTotal} box${boxTotal === 1 ? '' : 'es'}` : '')}
            </button>
            <button
                onClick={() => onPrintAllItems(run)}
                disabled={busy}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                title="FNSKU unit labels — select the DYMO 550 in the print dialog; silent printing only works to the station's default printer"
            >
                <TagIcon className="h-3.5 w-3.5" />
                {busyKey === 'ALL_ITEM' ? 'Printing…' : 'Print item labels (DYMO)'}
            </button>
            <button
                onClick={() => setOpen(o => !o)}
                disabled={busy}
                className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-50"
                title="More options: page type, open PDF"
            >
                <Cog6ToothIcon className="h-3.5 w-3.5" />
            </button>
            {open && (
                <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-md border border-gray-200 bg-white shadow-lg p-2">
                    <div className="flex items-center justify-between gap-2 px-1 pb-2">
                        <label className="text-[11px] text-gray-500">Box label page type</label>
                        <select
                            value={pageType}
                            onChange={(e) => onPageTypeChange(e.target.value)}
                            className="rounded border border-gray-300 px-1.5 py-1 text-xs text-gray-700"
                        >
                            {PAGE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={() => { onOpenAll(run); setOpen(false); }}
                        disabled={busy}
                        className="w-full text-left px-2 py-1.5 text-xs text-gray-600 hover:bg-indigo-50 rounded disabled:opacity-50"
                    >
                        open all box labels PDF
                    </button>
                </div>
            )}
        </div>
    );
}

// ---- Task 73 (own-carrier tracking entry) pure helpers -- exported for ConfirmedShipments.test.js ----
//
// An own-carrier shipment is confirmed with NO Amazon freight: Amazon never generated a carrier
// label and never learns the tracking unless the operator hands it back through
// updateShipmentTrackingDetails. Freight (LTL) wants a bill-of-lading number and PRO/freight-bill
// numbers; parcel wants one tracking number per box.

/** "PRO-1, PRO-2\nPRO-3" -> ["PRO-1","PRO-2","PRO-3"]. Splits on commas and whitespace, dedupes. */
export function parseFreightBillNumbers(text) {
    const seen = new Set();
    return String(text || '')
        .split(/[\s,;]+/)
        .map(s => s.trim())
        .filter(s => s && !seen.has(s) && seen.add(s));
}

/**
 * The POST body for /api/shipment-planner/tracking, or null when there is nothing to send.
 * Mirrors the backend's one-of rule (freight OR parcel, never both) so the button can be disabled
 * rather than the operator discovering the refusal after a round trip.
 */
export function buildTrackingPayload({ mode, bol, proText, boxTracking }) {
    if (mode === 'LTL') {
        const billOfLadingNumber = String(bol || '').trim();
        const freightBillNumbers = parseFreightBillNumbers(proText);
        if (!billOfLadingNumber && freightBillNumbers.length === 0) return null;
        return { billOfLadingNumber: billOfLadingNumber || null, freightBillNumbers };
    }
    const boxes = Object.entries(boxTracking || {})
        .map(([boxId, trackingId]) => ({ boxId, trackingId: String(trackingId || '').trim() }))
        .filter(b => b.trackingId);
    if (boxes.length === 0) return null;
    return { boxes };
}

/** What Amazon reports on the shipment after the write -- the accepted values, not the form's. */
export function describeAcceptedTracking(accepted) {
    if (!accepted) return '';
    const parts = [];
    if (accepted.ltl) {
        if (accepted.ltl.billOfLadingNumber) parts.push(`BOL ${accepted.ltl.billOfLadingNumber}`);
        const bills = accepted.ltl.freightBillNumber || [];
        if (bills.length > 0) parts.push(`PRO ${bills.join(', ')}`);
    }
    (accepted.boxes || []).forEach(b => {
        parts.push(`${b.boxId}: ${b.trackingId}${b.validationStatus ? ` (${b.validationStatus})` : ''}`);
    });
    return parts.join(' · ');
}

/**
 * Task 73: freight documents + tracking entry for one shipment.
 *
 * <p>Deliberately honest about what exists. Box labels come from Amazon for every shipment,
 * partnered or not. A partnered CARRIER label does not exist for an own-carrier shipment — Amazon
 * never bought the freight — so nothing here pretends to print one. What an own-carrier LTL
 * shipment does need is the bill of lading (v0 getBillOfLading), and what Amazon needs back is the
 * BOL/PRO or the per-box tracking.
 */
function TrackingPanel({ run, ship }) {
    const [mode, setMode] = useState('LTL');
    const [bol, setBol] = useState('');
    const [proText, setProText] = useState('');
    const [boxIds, setBoxIds] = useState(null);
    const [boxTracking, setBoxTracking] = useState({});
    const [saving, setSaving] = useState(false);
    const [accepted, setAccepted] = useState(null);
    const [bolBusy, setBolBusy] = useState(false);

    const payload = buildTrackingPayload({ mode, bol, proText, boxTracking });

    const loadBoxes = async () => {
        if (boxIds) return;
        try {
            const res = await ApiClient.get(`/api/shipment-planner/boxes/${run.planId}/${ship.shipmentId}`);
            setBoxIds(res.data?.boxIds || []);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not load this shipment\'s boxes');
            setBoxIds([]);
        }
    };

    const submit = async () => {
        if (!payload) return;
        setSaving(true);
        try {
            const res = await ApiClient.post(
                `/api/shipment-planner/tracking/${run.planId}/${ship.shipmentId}`, payload, { timeout: 60000 });
            setAccepted(res.data?.accepted || null);
            toast.success('Amazon accepted the tracking details');
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Tracking update failed');
        }
        setSaving(false);
    };

    const openBol = async () => {
        setBolBusy(true);
        try {
            const res = await ApiClient.get(
                `/api/shipment-planner/bill-of-lading/${run.planId}/${ship.shipmentId}`,
                { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            window.open(url, '_blank', 'noopener');
        } catch (err) {
            // The 404 body arrives as a Blob when responseType is blob -- decode it so the operator
            // sees "no BOL for a parcel shipment" instead of "[object Blob]".
            let msg = err.response?.data?.error;
            if (!msg && err.response?.data instanceof Blob) {
                try { msg = JSON.parse(await err.response.data.text())?.error; } catch { /* keep default */ }
            }
            toast.error(msg || 'Bill of lading unavailable');
        }
        setBolBusy(false);
    };

    return (
        <div data-testid="tracking-panel" className="w-full mt-2 pt-2 border-t border-gray-100">
            <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="text-gray-500">Own-carrier freight:</span>
                <label className="flex items-center gap-1 text-gray-600">
                    <input type="radio" checked={mode === 'LTL'} onChange={() => setMode('LTL')} />
                    Freight (BOL/PRO)
                </label>
                <label className="flex items-center gap-1 text-gray-600">
                    <input
                        type="radio"
                        checked={mode === 'SPD'}
                        onChange={() => { setMode('SPD'); loadBoxes(); }}
                    />
                    Parcel (per box)
                </label>
                <button
                    type="button"
                    onClick={openBol}
                    disabled={bolBusy}
                    className="text-indigo-600 hover:underline disabled:opacity-50"
                    title="Amazon's bill of lading (freight shipments only — a parcel shipment never gets one)"
                >
                    {bolBusy ? 'Fetching…' : 'Bill of lading (PDF)'}
                </button>
            </div>

            {mode === 'LTL' ? (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    <input
                        aria-label="Bill of lading number"
                        placeholder="Bill of lading number"
                        value={bol}
                        onChange={e => setBol(e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded w-56"
                    />
                    <input
                        aria-label="PRO / freight bill numbers"
                        placeholder="PRO / freight bill numbers (comma separated)"
                        value={proText}
                        onChange={e => setProText(e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded w-72"
                    />
                </div>
            ) : (
                <div className="mt-2 space-y-1">
                    {boxIds === null && <span className="text-xs text-gray-400">Loading boxes…</span>}
                    {boxIds?.length === 0 && (
                        <span className="text-xs text-gray-400">Amazon has no boxes registered for this shipment yet.</span>
                    )}
                    {(boxIds || []).map(boxId => (
                        <div key={boxId} className="flex items-center gap-2">
                            <span className="font-mono text-[11px] text-gray-500 w-40 truncate">{boxId}</span>
                            <input
                                aria-label={`Tracking number for ${boxId}`}
                                placeholder="tracking number"
                                value={boxTracking[boxId] || ''}
                                onChange={e => setBoxTracking(prev => ({ ...prev, [boxId]: e.target.value }))}
                                className="px-2 py-1 text-xs border border-gray-300 rounded w-56"
                            />
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-3 mt-2">
                <button
                    type="button"
                    onClick={submit}
                    disabled={saving || !payload}
                    className="px-3 py-1 text-xs font-medium rounded-md bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50"
                    title={payload ? 'Sends these details to Amazon (updateShipmentTrackingDetails)'
                                   : 'Enter a BOL/PRO number, or a tracking number for at least one box'}
                >
                    {saving ? 'Sending…' : 'Send tracking to Amazon'}
                </button>
                {accepted && (
                    <span data-testid="tracking-accepted" className="text-xs text-green-700">
                        Amazon has: {describeAcceptedTracking(accepted) || '(nothing recorded)'}
                    </span>
                )}
            </div>
        </div>
    );
}

// One shipment row: destination, FBA confirmation id, units/boxes, and the print controls.
// Task 57: primary action defaults to every box for this shipment (thermal 4×6); the page-type
// override collapses behind the gear icon instead of a picker always on-screen. A DYMO-550
// "item labels" (FNSKU) button sits beside the box-label button (owner addition #2).
function ShipmentRow({ run, ship, printingKey, openingKey, printingItemKey, printingPalletKey,
                       onPrint, onOpen, onPrintItem, onPrintPallet }) {
    const [pageType, setPageType] = useState(DEFAULT_LABEL_PAGE_TYPE);
    const [showAdvanced, setShowAdvanced] = useState(false);
    // Task 73: collapsed by default -- only own-carrier shipments need it, and most don't.
    const [showTracking, setShowTracking] = useState(false);
    const key = `${run.planId}:${ship.shipmentId}`;
    const printing = printingKey === key;
    const opening = openingKey === key;
    const printingItem = printingItemKey === key;
    const printingPallet = printingPalletKey === key;
    // Task C8: only a freight shipment carries a pallet build -- parcel shipments have no
    // `pallets` at all, so the button is simply absent rather than disabled.
    const palletQuantity = ship.pallets?.quantity > 0 ? ship.pallets.quantity : null;
    const cancelled = String(run.status).toUpperCase() !== 'CONFIRMED';
    const boxCount = ship.boxCount ?? ship.units ?? 1;
    // Task 77: a shipment that's left the building (SHIPPED/IN_TRANSIT/DELIVERED/RECEIVING/CLOSED)
    // renders muted -- labels are still printable (reprints happen) but visually secondary.
    const greyed = !cancelled && isShipmentGreyed(ship);

    return (
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2 border rounded-md ${
            greyed ? 'border-gray-100 bg-gray-50/70' : 'border-gray-200 bg-white'
        }`}>
            <div className="min-w-[10rem]">
                {/* Task 89: the name we gave Amazon at confirm time -- the SAME label Seller
                    Central's Send-to-Amazon workflow list shows, so the operator matching a pallet
                    to a screen is reading one string, not translating between two. Absent on runs
                    confirmed before task 89 (and on any whose rename call failed), in which case
                    nothing renders rather than a guessed label. */}
                {ship.shipmentName && (
                    <div
                        className={`text-xs font-semibold truncate ${greyed ? 'text-gray-400' : 'text-gray-900'}`}
                        title={ship.shipmentName}
                    >
                        {ship.shipmentName}
                    </div>
                )}
                <div className={`font-mono text-xs ${greyed ? 'text-gray-400' : 'text-gray-700'}`}>
                    {ship.destinationFc || '—'}
                    {(ship.destinationCity || ship.destinationState) && (
                        <span className="text-gray-400 font-sans">
                            {' '}· {[ship.destinationCity, ship.destinationState].filter(Boolean).join(', ')}
                        </span>
                    )}
                </div>
                <div className="font-mono text-[11px] text-gray-400" title={ship.shipmentId}>
                    {ship.shipmentConfirmationId || 'no FBA id yet'}
                </div>
                {ship.shipmentStatus && (
                    <div className="mt-0.5"><ShipmentStatusPill status={ship.shipmentStatus} /></div>
                )}
            </div>
            <div className={`text-xs whitespace-nowrap ${greyed ? 'text-gray-400' : 'text-gray-600'}`}>
                {ship.units ?? '—'} units
                {ship.boxCount != null && <span className="text-gray-400"> · {ship.boxCount} boxes</span>}
            </div>
            {!cancelled && (
                <div className="relative flex items-center gap-2 ml-auto">
                    <button
                        onClick={() => onPrint(run, ship, pageType)}
                        disabled={printing || opening || printingItem}
                        className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-50 ${
                            greyed
                                ? 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }`}
                        title="Prints every box's label for this shipment in one dialog, 4×6 thermal by default"
                    >
                        <PrinterIcon className="h-3.5 w-3.5" />
                        {printing ? 'Printing…' : `Print all labels (${boxCount})`}
                    </button>
                    <button
                        onClick={() => onPrintItem(run, ship)}
                        disabled={printing || opening || printingItem}
                        className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md border disabled:opacity-50 ${
                            greyed
                                ? 'border-gray-200 text-gray-400 hover:bg-gray-100'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                        title="FNSKU unit labels — select the DYMO 550 in the print dialog; silent printing only works to the station's default printer"
                    >
                        <TagIcon className="h-3.5 w-3.5" />
                        {printingItem ? 'Printing…' : 'Item labels (DYMO)'}
                    </button>
                    {palletQuantity != null && (
                        <button
                            onClick={() => onPrintPallet(run, ship)}
                            disabled={printing || opening || printingItem || printingPallet}
                            className={`flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md border disabled:opacity-50 ${
                                greyed
                                    ? 'border-gray-200 text-gray-400 hover:bg-gray-100'
                                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                            title="Four labels per pallet, one per side -- straight from Amazon, printed only after freight was priced from the measured pallet build"
                        >
                            <Square3Stack3DIcon className="h-3.5 w-3.5" />
                            {printingPallet ? 'Printing…' : `Pallet labels (${palletQuantity})`}
                        </button>
                    )}
                    <button
                        onClick={() => setShowAdvanced(v => !v)}
                        disabled={printing || opening || printingItem}
                        className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-50"
                        title="More options: page type, open PDF"
                    >
                        <Cog6ToothIcon className="h-3.5 w-3.5" />
                    </button>
                    {showAdvanced && (
                        <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded-md border border-gray-200 bg-white shadow-lg p-2 flex flex-col gap-2">
                            <label className="text-[11px] text-gray-500 flex flex-col gap-0.5">
                                Page type
                                <select
                                    value={pageType}
                                    onChange={(e) => setPageType(e.target.value)}
                                    className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700"
                                >
                                    {PAGE_TYPES.map(pt => (
                                        <option key={pt.value} value={pt.value}>{pt.label}</option>
                                    ))}
                                </select>
                            </label>
                            <button
                                onClick={() => { onOpen(run, ship, pageType); setShowAdvanced(false); }}
                                disabled={printing || opening || printingItem}
                                className="text-left text-[11px] text-gray-500 hover:text-indigo-600 hover:underline disabled:opacity-50"
                            >
                                {opening ? 'Fetching…' : 'open box labels PDF'}
                            </button>
                        </div>
                    )}
                </div>
            )}
            {cancelled && (
                <span className="ml-auto text-xs text-gray-400">shipment voided — labels unavailable</span>
            )}
            {!cancelled && (
                <>
                    <button
                        type="button"
                        onClick={() => setShowTracking(v => !v)}
                        className="w-full text-left text-[11px] text-gray-400 hover:text-indigo-600"
                    >
                        {showTracking ? '− hide' : '+ show'} own-carrier freight docs &amp; tracking
                    </button>
                    {showTracking && <TrackingPanel run={run} ship={ship} />}
                </>
            )}
        </div>
    );
}

export default function ConfirmedShipments() {
    const navigate = useNavigate();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [printingKey, setPrintingKey] = useState(null);
    const [openingKey, setOpeningKey] = useState(null);
    const [printingItemKey, setPrintingItemKey] = useState(null);
    const [printingPalletKey, setPrintingPalletKey] = useState(null);
    // Run-level "print all shipments" busy state: `${runId}:ALL` (box) or `${runId}:ALL_ITEM`.
    const [printingAllKey, setPrintingAllKey] = useState(null);
    const [allPageType, setAllPageType] = useState(DEFAULT_LABEL_PAGE_TYPE);
    const [cancelTarget, setCancelTarget] = useState(null);
    // Task 57 owner addition #3 (verbatim): "we need to be able to show only confirmed shipments
    // and exclude cancelled." Off by default -- GET /confirmed already excludes cancelled runs
    // unless includeCancelled=true is passed.
    const [showCancelled, setShowCancelled] = useState(false);

    const load = useCallback(async (includeCancelled) => {
        setLoading(true);
        setError(null);
        try {
            const res = await ApiClient.get('/api/shipment-planner/confirmed', {
                params: { includeCancelled: !!includeCancelled },
            });
            setRuns(res.data || []);
        } catch (err) {
            console.error('Failed to load confirmed shipments:', err);
            setError(err.response?.data?.error || err.message || 'Failed to load confirmed shipments');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(showCancelled); }, [load, showCancelled]);

    // Owner, 2026-09-02: rename through the UI. The operator's words go to the plan AND every
    // shipment in Seller Central (Amazon caps plan names at 40 chars — the server refuses longer
    // rather than truncating). Partial refusals are reported by name, never swallowed.
    const renameRun = async (run) => {
        const name = window.prompt(
            'New name for this shipment plan (max 40 characters).\nApplied to the plan and every shipment in Seller Central.',
            run.planName || '');
        if (name == null || !name.trim()) return;
        try {
            const res = await ApiClient.post(`/api/shipment-planner/runs/${run.runId}/rename`, { name: name.trim() });
            const failed = res.data?.failed || {};
            const failures = Object.keys(failed);
            if (failures.length > 0) {
                toast.error(`Renamed, but Amazon refused ${failures.length}: ${failures.join(', ')} — ${failed[failures[0]]}`);
            } else {
                toast.success(`Renamed to "${res.data?.applied?.plan || name.trim()}"`);
            }
            load(showCancelled);
        } catch (err) {
            toast.error(err.response?.data?.error || err.message || 'Rename failed');
        }
    };

    // Task 49: fetch/blob/error-decode logic now lives in the shared PrintLabelsAction helpers so
    // the Shipment Runs table and the Shipment Planner loaded-run banner can reuse the exact same
    // print behavior this page originated. Task 52: printLabels goes straight to the browser
    // print command (no tab); openLabels keeps open-in-a-new-tab as the "open PDF" affordance.
    const printLabels = async (run, ship, pageType) => {
        const key = `${run.planId}:${ship.shipmentId}`;
        setPrintingKey(key);
        await printShipmentLabels(run.planId, ship.shipmentId, { pageType });
        setPrintingKey(null);
    };

    const openLabels = async (run, ship, pageType) => {
        const key = `${run.planId}:${ship.shipmentId}`;
        setOpeningKey(key);
        await openShipmentLabelsPdf(run.planId, ship.shipmentId, { pageType });
        setOpeningKey(null);
    };

    const printItemLabels = async (run, ship) => {
        const key = `${run.planId}:${ship.shipmentId}`;
        setPrintingItemKey(key);
        await printShipmentItemLabels(run.planId, ship.shipmentId);
        setPrintingItemKey(null);
    };

    // Task C8: pallet labels -- the backend derives numberOfPallets from the shipment's own
    // measured build and 400s (with an explanatory message rendered verbatim) if this run's
    // freight was never priced from measured pallets.
    const printPallet = async (run, ship) => {
        const key = `${run.planId}:${ship.shipmentId}`;
        setPrintingPalletKey(key);
        await printPalletLabels(run.planId, ship.shipmentId);
        setPrintingPalletKey(null);
    };

    // Task 57 (owner, plan-level "print all shipments"): one fetch, one merged PDF, one print
    // dialog covering every box across every shipment in the workflow.
    const printAllLabels = async (run) => {
        setPrintingAllKey(`${run.runId}:ALL`);
        await printAllShipmentLabels(run.planId, { pageType: allPageType, totalCount: (run.shipments || []).length });
        setPrintingAllKey(null);
    };

    const openAllLabels = async (run) => {
        setPrintingAllKey(`${run.runId}:ALL`);
        await openAllShipmentLabelsPdf(run.planId, { pageType: allPageType, totalCount: (run.shipments || []).length });
        setPrintingAllKey(null);
    };

    const printAllItemLabels = async (run) => {
        setPrintingAllKey(`${run.runId}:ALL_ITEM`);
        await printAllShipmentItemLabels(run.planId, { totalCount: (run.shipments || []).length });
        setPrintingAllKey(null);
    };

    return (
        // Task 86: same fix as ShipmentRuns.js -- SidebarLayout.js already wraps every routed page
        // in its own "px-4 sm:px-6 lg:px-8" padding, so repeating it here plus a hard max-w-7xl
        // (1280px) cap doubled the padding and threw away everything a wide monitor offered beyond
        // 1280px, on a page whose table (Products/Destinations/Shipping/Actions) needs the room.
        <div className="py-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <PrinterIcon className="h-8 w-8 text-indigo-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Confirmed Shipments</h1>
                        <p className="text-sm text-gray-500">
                            Amazon-accepted shipments — print every box's combined UPS + FBA label PDF here.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-sm text-gray-600 select-none">
                        <input
                            type="checkbox"
                            checked={showCancelled}
                            onChange={(e) => setShowCancelled(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Show cancelled
                    </label>
                    <button
                        onClick={() => load(showCancelled)}
                        disabled={loading}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {loading && runs.length === 0 && <div className="text-gray-500">Loading…</div>}
            {error && <div className="text-red-600 mb-4">Error: {error}</div>}

            {!error && !loading && runs.length === 0 && (
                <div className="text-center text-gray-400 bg-white border border-gray-200 rounded-md py-14">
                    No confirmed shipments yet.
                </div>
            )}

            {!error && runs.length > 0 && (
                <div className="overflow-x-auto bg-white border border-gray-200 rounded-md shadow-sm">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Confirmed</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Products</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Units</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Destinations</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Shipping</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                                {/* Task 86: sticky, same treatment as ShipmentRuns.js's tables --
                                    Actions must never scroll out of view. */}
                                <th className="sticky right-0 bg-gray-50 px-3 py-2 text-left text-xs font-semibold text-gray-700 border-l border-gray-200">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {runs.map(run => {
                                // Task 77: active (still-being-prepped) shipments first, then
                                // greyed (left-the-building) ones by most-recent status change.
                                const shipments = sortShipmentsForDisplay(run.shipments || []);
                                const isConfirmed = String(run.status).toUpperCase() === 'CONFIRMED';
                                const busyKey = printingAllKey === `${run.runId}:ALL` ? 'ALL'
                                    : printingAllKey === `${run.runId}:ALL_ITEM` ? 'ALL_ITEM' : null;
                                const boxTotal = shipments.every(s => s.boxCount != null)
                                    ? shipments.reduce((sum, s) => sum + s.boxCount, 0)
                                    : null;
                                const destinationsLabel = shipments.map(s => s.destinationFc).filter(Boolean).join(', ') || '—';
                                return (
                                <React.Fragment key={run.runId}>
                                    <tr className="group hover:bg-gray-50">
                                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                            {formatDateTime(run.confirmedAt)}
                                            <div className="font-mono text-[11px] text-gray-400" title={run.planId || ''}>
                                                Run #{run.runId}
                                            </div>
                                            {/* Task 89: the inbound PLAN's name, i.e. what this
                                                workflow is called in Seller Central. Trimmed to 40
                                                chars by Amazon's limit, so it is always short. */}
                                            {run.planName && (
                                                <div
                                                    className="text-[11px] font-medium text-gray-600 max-w-[12rem] truncate"
                                                    title={run.planName}
                                                >
                                                    {run.planName}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2"><ProductsCell asins={run.asins} /></td>
                                        <td className="px-3 py-2 text-right text-gray-700">{run.totalUnits ?? '—'}</td>
                                        <td className="px-3 py-2 font-mono text-xs text-gray-600 max-w-[9rem] truncate" title={destinationsLabel}>
                                            {destinationsLabel}
                                        </td>
                                        <td className="px-3 py-2">
                                            <ShippingMethodChip method={run.shippingMethod} />
                                            <ShippingEconomics run={run} />
                                        </td>
                                        <td className="px-3 py-2"><StatusPill status={run.status} /></td>
                                        <td className="sticky right-0 px-3 py-2 bg-white group-hover:bg-gray-50 border-l border-gray-100">
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => navigate(`/shipment-planner?run=${run.runId}`)}
                                                    className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                                                >
                                                    Open run
                                                </button>
                                                {isConfirmed && run.planId && (
                                                    <button
                                                        onClick={() => renameRun(run)}
                                                        className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                                                        title="Rename the plan and every shipment in Seller Central"
                                                    >
                                                        Rename
                                                    </button>
                                                )}
                                                {isConfirmed && run.planId && (
                                                    <button
                                                        onClick={() => setCancelTarget(run)}
                                                        className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                                    >
                                                        Cancel shipment
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={7} className="px-3 pb-3 pt-0 bg-gray-50/50">
                                            <div className="flex flex-col gap-1.5">
                                                {shipments.length === 0 && (
                                                    <div className="text-xs text-gray-400 px-1">No shipment details recorded for this run.</div>
                                                )}
                                                {isConfirmed && shipments.length > 1 && (
                                                    <PrintAllBar
                                                        run={run}
                                                        shipmentCount={shipments.length}
                                                        boxTotal={boxTotal}
                                                        busyKey={busyKey}
                                                        onPrintAll={printAllLabels}
                                                        onPrintAllItems={printAllItemLabels}
                                                        onOpenAll={openAllLabels}
                                                        pageType={allPageType}
                                                        onPageTypeChange={setAllPageType}
                                                    />
                                                )}
                                                {shipments.map(ship => (
                                                    <ShipmentRow
                                                        key={ship.shipmentId}
                                                        run={run}
                                                        ship={ship}
                                                        printingKey={printingKey}
                                                        openingKey={openingKey}
                                                        printingItemKey={printingItemKey}
                                                        printingPalletKey={printingPalletKey}
                                                        onPrint={printLabels}
                                                        onOpen={openLabels}
                                                        onPrintItem={printItemLabels}
                                                        onPrintPallet={printPallet}
                                                    />
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <CancelConfirmedShipmentModal
                open={!!cancelTarget}
                onClose={() => setCancelTarget(null)}
                planId={cancelTarget?.planId}
                runLabel={cancelTarget ? `Run #${cancelTarget.runId}` : null}
                units={cancelTarget?.totalUnits}
                asins={cancelTarget?.asins}
                confirmedAt={cancelTarget?.confirmedAt}
                onCancelled={() => {
                    setCancelTarget(null);
                    toast.success('Shipment cancelled.');
                    load(showCancelled);
                }}
            />
        </div>
    );
}
